import { ReactElement, useState, useEffect } from 'react';

import _groupBy from 'lodash/groupBy';
import debounce from 'lodash/debounce';
import * as styles from './styles.css';
import { Autocomplete, Avatar, Checkbox, Chip, Tooltip } from '@mui/material';
import { IInternalMultiSelectProps } from './index';
import BaseInput from '../input/base-input';
import Clear from '@mui/icons-material/Clear';
import { IOption } from '../../../interfaces';
import { matchSorter } from 'match-sorter';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import { theme } from '../../../global.css';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const BaseMultiSelect = ({
  id,
  className,
  label,
  options,
  value,
  onSelect,
  placeholder,
  onChange,
  disabled,
  required,
  groupBy,
  maxReached,
  error,
  warn,
}: IInternalMultiSelectProps) => {
  const onDelete = (option: IOption) => {
    const ids = value.filter((o) => {
      return o.id !== option.id;
    });
    onSelect(ids);
  };
  const onDeleteGroup = (group: string) => {
    const ids = value.filter(({ parentName }) => group !== parentName);
    onSelect(ids);
  };
  const [showGroup, setShowGroup] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue, setDebouncedInputValue] = useState('');

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedInputValue(inputValue);
    }, 300);

    handler();
    return () => {
      handler.cancel();
    };
  }, [inputValue]);

  let sortKeys = [{ threshold: matchSorter.rankings.MATCHES, key: 'label' }];
  if (groupBy) {
    sortKeys = [{ threshold: matchSorter.rankings.MATCHES, key: 'parentName' }, ...sortKeys];
  }

  const filterOptions = (options): IOption[] => {
    return matchSorter(options, debouncedInputValue, {
      keys: sortKeys,
    });
  };

  const checkOption = (option: IOption) => value.some(({ id }) => option.id === id);

  const groups =
    _groupBy(value, function (n) {
      return n.parentName;
    }) || {};

  let groupByParams = {};
  if (groupBy) {
    groupByParams = {
      disableCloseOnSelect: true,
      filterSelectedOptions: false,
      groupBy: (option: IOption) => option.parentName,
      renderGroup: (props) => (
        <GroupList {...props} openGroup={showGroup} groups={groups}></GroupList>
      ),
      renderOption: (props, option) => (
        <li {...props} key={`${id}-${option.id}-${option.label}`}>
          <Checkbox
            key={option.id}
            icon={icon}
            checkedIcon={checkedIcon}
            checked={checkOption(option)}
          />
          <span>{option.label}</span>
        </li>
      ),
    };
  }

  return (
    <div className={styles.root}>
      <Autocomplete
        multiple
        id={id}
        className={className ?? styles.autocomplete}
        value={value}
        onBlur={() => {
          setInputValue('');
          setShowGroup(false);
        }}
        getOptionDisabled={(option) => !!maxReached && !value.includes(option)}
        onChange={(_, value) => onSelect(value ?? '')}
        options={options}
        filterOptions={filterOptions}
        getOptionLabel={(option: IOption) => option.label}
        isOptionEqualToValue={(option: IOption, value: IOption) =>
          option.id.toString() === value.id.toString()
        }
        renderOption={(props, option) => {
          return (
            <li {...props} key={`${id}-${option.id}-${option.label}`}>
              <span>{option.label}</span>
            </li>
          );
        }}
        filterSelectedOptions
        renderTags={() => null}
        renderInput={(params) => {
          const changeHandler = (value) => {
            setInputValue(value);
            setShowGroup(value.length !== 0);
            onChange && onChange(value);
          };
          return (
            <BaseInput
              {...params}
              required={required}
              placeholder={placeholder}
              label={label}
              onChange={changeHandler}
              error={error}
              disabled={disabled}
            />
          );
        }}
        {...groupByParams}
      />
      {warn && <span className={styles.warn}>{warn}</span>}
      <div className={styles.chips}>
        {groupBy ? (
          <GroupChips id={id} onDeleteGroup={onDeleteGroup} groups={groups} />
        ) : (
          value.map((option) => {
            return (
              <Chip
                key={`${id}-${option.id}-${option.label}-chip`}
                classes={{ root: styles.chip, deleteIcon: styles.chip_icon }}
                id={option.id}
                label={option.label}
                onClick={() => {
                  onDelete(option);
                }}
                onDelete={() => {
                  onDelete(option);
                }}
                deleteIcon={<Clear />}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

export default BaseMultiSelect;

interface IGroupChipsProps {
  id: string;
  groups: { string: IOption[] };
  onDeleteGroup: (group: string) => void;
}

const GroupChips = ({ id, groups, onDeleteGroup }: IGroupChipsProps) => {
  return (
    <>
      {Object.keys(groups).map((groupName) => {
        const group = groups[groupName];
        const labels = group.map(({ id, label }) => <div key={id}>{label}</div>);

        return (
          <Tooltip key={`${id}-${groupName}-tooltip`} title={labels}>
            <Chip
              classes={{ root: styles.chip, deleteIcon: styles.chip_icon }}
              id={groupName}
              avatar={<Avatar style={{ color: theme.colors.ink.dark }}>{group.length}</Avatar>}
              label={groupName}
              onDelete={() => onDeleteGroup(groupName)}
              deleteIcon={<Clear />}
            />
          </Tooltip>
        );
      })}
    </>
  );
};

interface IGroupListProps {
  group: string;
  groups: { string: IOption[] };
  children: ReactElement;
  openGroup: boolean;
}

const GroupList = ({ group, groups, children, openGroup }: IGroupListProps) => {
  const [showGroup, setShowGroup] = useState(openGroup);
  let itemsCount;

  if (groups && groups[group]) {
    itemsCount = groups[group].length;
  }
  return (
    <>
      <div onClick={() => setShowGroup(!showGroup)} className={styles.group_list}>
        <div>
          <span>{group}</span>
          {itemsCount && (
            <span style={{ display: 'inline-flex' }}>
              <Avatar className={styles.small_avatar}>{itemsCount}</Avatar>
            </span>
          )}
        </div>
        {showGroup ? (
          <KeyboardArrowDownIcon fontSize="small" />
        ) : (
          <KeyboardArrowRightIcon fontSize="small" />
        )}
      </div>
      {showGroup && children}
    </>
  );
};
